// Approved member-app environments in release order DEV → TEST → PROD.
// core: null = no Core assignment confirmed by Control OS yet (blocker, login stays locked).
// legacy: explicit, same-environment transition pairs (app host + Core), kept until the planned host is connected.
export const ENVIRONMENTS = {
  preview: { label: "Vorschau", appHost: null, core: null, coreEnv: "preview" },
  development: { label: "DEV", appHost: "devvisibilis.visibilis.life", core: null, coreEnv: null },
  test: { label: "TEST", appHost: "testvisibilis.visibilis.life", core: null, coreEnv: null },
  production: {
    label: "PROD", appHost: "prodvisibilis.visibilis.life", core: "https://control.visibilis.life", coreEnv: "production",
    legacy: [{ appHost: "app.visibilis.life", core: "https://control.visibilis.life" }],
  },
};

const ALIASES = { dev: "development", prod: "production" };
const PREVIEW_HOST = /\.preview\.emergentagent\.com$/;
const LOCAL_HOST = /^(localhost|127\.0\.0\.1)$/;

// Separate test profile, only for the authorised local throw-away stack. It exists in the development server only:
// NODE_ENV is replaced at build time, so a production bundle contains none of it (build-profile.cjs also refuses it).
const LOCAL_ENABLED = process.env.NODE_ENV === "development";
export const LOCAL_ISOLATED = LOCAL_ENABLED ? Object.freeze({
  app: "http://127.0.0.1:3003",
  core: "http://127.0.0.1:8002",
  origins: Object.freeze(["http://127.0.0.1:3002", "http://127.0.0.1:3003", "http://127.0.0.1:3004"]),
}) : null;
const STACK_ID = /^[0-9a-f]{16}$/;

function parse(url) {
  try { return new URL(url); } catch (_) { return null; }
}

// Exact values only: no other IP spelling, no localhost, no other port, scheme or host.
function resolveLocal({ apiUrl, authHeader, origin, stackId }) {
  const problems = [];
  if (origin !== LOCAL_ISOLATED.app) problems.push("host_mismatch");
  if (apiUrl !== LOCAL_ISOLATED.core) problems.push("core_mismatch");
  if (String(authHeader || "").toLowerCase() !== "authorization") problems.push("transport");
  const id = typeof stackId === "string" && STACK_ID.test(stackId) ? stackId : null;
  if (!id) problems.push("stack_id_missing");
  return {
    key: "local_isolated", label: "Lokaler Wegwerf-Teststack", problems, core: String(apiUrl || ""), appHost: parse(origin)?.host || "",
    expectedHost: "127.0.0.1:3003", legacy: false, coreEnv: null, stackId: id,
  };
}

// Pure check of build configuration against the page origin. Never reads secrets.
export function resolveEnvironment({ appEnv, apiUrl, authHeader, origin, nodeEnv, stackId }) {
  const raw = String(appEnv || "preview").trim().toLowerCase();
  const key = ALIASES[raw] || raw;
  if (key === "local_isolated") {
    return LOCAL_ENABLED && nodeEnv === "development" ? resolveLocal({ apiUrl, authHeader, origin, stackId })
      : { key, label: raw, problems: ["local_not_dev"], core: "", appHost: "", expectedHost: null, coreEnv: null };
  }
  const def = ENVIRONMENTS[key];
  const core = String(apiUrl || "").trim().replace(/\/+$/, "");
  const page = parse(origin);
  const appHost = page ? page.host : "";
  const coreUrl = parse(core);
  const problems = [];
  let pairs = null;
  let pair = null;
  if (!def) return { key, label: raw, problems: ["env_unknown"], core, appHost, expectedHost: null, coreEnv: null };

  if (!core) problems.push("core_missing");
  else if (!coreUrl || coreUrl.protocol !== "https:" || coreUrl.origin !== core) problems.push("core_invalid");

  if (key === "preview") {
    if (!(PREVIEW_HOST.test(page?.hostname || "") || LOCAL_HOST.test(page?.hostname || ""))) problems.push("host_mismatch");
    if (coreUrl && !PREVIEW_HOST.test(coreUrl.hostname)) problems.push("core_mismatch");
  } else {
    pairs = [{ appHost: def.appHost, core: def.core }, ...(def.legacy || [])];
    pair = page && page.protocol === "https:" ? pairs.find((p) => p.appHost === appHost) : null;
    if (!pair) problems.push("host_mismatch");
    const expectedCore = pair ? pair.core : def.core;
    if (!expectedCore) problems.push("core_unassigned");
    else if (core && core !== expectedCore) problems.push("core_mismatch");
    if (String(authHeader || "").toLowerCase() !== "authorization") problems.push("transport");
  }

  return {
    key, label: def.label, problems, core, appHost,
    expectedHost: pairs ? pairs.map((p) => p.appHost).join(" oder ") : "*.preview.emergentagent.com",
    legacy: Boolean(pair && pairs && pair !== pairs[0]),
    coreEnv: def.coreEnv,
  };
}

// Validates the Core's public status document (GET /api/) against the expected environment.
export function coreIdentityProblem(env, data) {
  if (LOCAL_ENABLED && env.key === "local_isolated") return localStackProblem(env, data);
  if (!data || typeof data.service !== "string" || !data.service.includes("Shared Core")) return "core_identity";
  if (env.coreEnv && data.env !== env.coreEnv) return "core_env_mismatch";
  return null;
}

// Plain JSON object (realm-independent) with exactly these own keys.
const plain = (o) => o !== null && typeof o === "object" && !Array.isArray(o) && Object.getPrototypeOf(Object.getPrototypeOf(o)) === null;
const keysAre = (o, keys) => plain(o) && Object.keys(o).sort().join("|") === keys.join("|");

// Complete isolation schema of the local stack (Core scripts/iso_stack_entry.py) and the explicitly injected stack id.
function localStackProblem(env, data) {
  if (!keysAre(data, ["isolated_stack", "service"]) || data.service !== "visibilis-core") return "stack_identity";
  const s = data.isolated_stack;
  if (!keysAre(s, ["bind", "database", "id", "mail", "origins"])) return "stack_schema";
  if (!env.stackId || typeof s.id !== "string" || !STACK_ID.test(s.id) || s.id !== env.stackId) return "stack_mismatch";
  const o = s.origins;
  if (s.database !== `iso_ui_${env.stackId.slice(0, 10)}` || s.bind !== "127.0.0.1" || s.mail !== "aus"
    || !Array.isArray(o) || o.length !== LOCAL_ISOLATED.origins.length || LOCAL_ISOLATED.origins.some((x, i) => o[i] !== x)) return "stack_schema";
  return null;
}

export const ENV_PROBLEM_TEXT = {
  env_unknown: "Die Umgebung (APP_ENV) ist unbekannt.",
  core_missing: "Es ist keine Core-Adresse konfiguriert.",
  core_invalid: "Die Core-Adresse ist ungültig (nur ein https-Ursprung ohne Pfad ist erlaubt).",
  host_mismatch: "Diese Adresse gehört nicht zur Umgebung {label} (erwartet: {expectedHost}).",
  core_unassigned: "Für {label} ist noch kein Core zugeordnet.",
  core_mismatch: "Die Core-Adresse passt nicht zur Umgebung {label}.",
  transport: "Für {label} muss das Token im Authorization-Header übertragen werden.",
  core_identity: "Unter der Core-Adresse antwortet kein Visibilis Core.",
  core_env_mismatch: "Der Core meldet eine andere Umgebung als {label}.",
  local_not_dev: "Das lokale Testprofil ist nur im Entwicklungsstart verfügbar.",
  stack_id_missing: "Die erwartete Stack-Kennung (16 kleine Hex-Zeichen) ist nicht konfiguriert.",
  stack_identity: "Unter der Core-Adresse antwortet kein isolierter Visibilis-Teststack.",
  stack_schema: "Die Isolationsangaben des Cores sind unvollständig oder weichen ab.",
  stack_mismatch: "Der Core meldet eine andere Stack-Kennung als erwartet.",
};

export const ENV = resolveEnvironment({
  appEnv: process.env.REACT_APP_ENV,
  apiUrl: process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL,
  authHeader: process.env.REACT_APP_MEMBER_AUTH_HEADER,
  origin: typeof window !== "undefined" ? window.location.origin : "",
  nodeEnv: process.env.NODE_ENV,
  stackId: process.env.REACT_APP_LOCAL_STACK_ID,
});

// True only in the development server with the local throw-away profile.
export const LOCAL_PROFILE = LOCAL_ENABLED && ENV.key === "local_isolated";

// Runtime result of the Core identity check; requests stay blocked until the login gate verified the Core.
export const coreState = { problem: "core_unchecked" };

// Keep the warning unless this is a fully consistent production build.
export const SHOW_PREVIEW_BADGE = !(ENV.key === "production" && ENV.problems.length === 0);
